import { getAccessToken, setAccessToken, clearAuth } from './auth-store.ts';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public payload: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  skipAuth?: boolean;
}

async function rawRequest(path: string, opts: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = {};
  // Only set Content-Type when there is actually a body to send.
  // Fastify 5 rejects requests with `Content-Type: application/json` and
  // an empty body (e.g. POST /auth/logout) — symptom:
  //   "Body cannot be empty when content-type is set to 'application/json'"
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (!opts.skipAuth) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
}

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async (): Promise<boolean> => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { accessToken: string };
      setAccessToken(data.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let response = await rawRequest(path, opts);

  if (response.status === 401 && !opts.skipAuth) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await rawRequest(path, opts);
    } else {
      clearAuth();
      throw new ApiError(401, 'Session expired', null);
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    // Surface zod issue messages instead of the generic "Validation failed"
    // wrapper from apps/api/src/plugins/errors.ts. The wrapper sends
    // { error: 'Validation failed', issues: [{message, path, ...}, …] };
    // we want the moderator to see the actual issue text.
    let message: string;
    if (
      isJson &&
      payload &&
      typeof payload === 'object' &&
      'issues' in payload &&
      Array.isArray((payload as { issues: unknown }).issues) &&
      (payload as { issues: unknown[] }).issues.length > 0
    ) {
      const issues = (payload as { issues: Array<{ message?: string; path?: unknown[] }> }).issues;
      message = issues
        .map((i) => {
          const path = Array.isArray(i.path) && i.path.length > 0 ? `${i.path.join('.')}: ` : '';
          return `${path}${i.message ?? 'invalid'}`;
        })
        .join('; ');
    } else if (isJson && payload && typeof payload === 'object' && 'error' in payload) {
      message = String((payload as { error: unknown }).error);
    } else {
      message = response.statusText;
    }
    throw new ApiError(response.status, message, payload);
  }

  return payload as T;
}
